import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@comps/ui/select";
import { FieldType } from "@dto/fields";
import { AND, EQUAL, Filter, HAS, IS_EMPTY, IS_NOT_EMPTY, NOT_EQUAL, NOT_HAS, OR, Operator } from "@dto/records";
import { getFieldKey, getOperatorLabel } from "@utils/model";
import { TW_FilterEditorOperatorSelect, TW_FilterEditorOperatorSelectFull, TW_FilterEditorValue_Wrapper } from "@utils/twClasses";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useModelStore } from "src/store";
import { ChoiceSelect } from "./ChoiceSelect";

interface ChoiceFilterValueSelectorProps {
  selectedPathKey: string | undefined;
  filter: Filter;
  validOperators: Operator[];
  validateFilter: (filter: Filter) => void;
  disabled: boolean
}

export function ChoiceFilterValueSelector({ selectedPathKey, filter, validOperators, validateFilter, disabled }: ChoiceFilterValueSelectorProps) {

  const { t } = useTranslation();
  const [selectedOperator, setSelectedOperator] = useState<Operator | undefined>(HAS);
  const [selectedFieldChoices, setSelectedFieldChoices] = useState<string[] | undefined>(undefined);
  const fieldByKeys = useModelStore((state) => state.fieldByKeys);

  useEffect(() => {
    if (filter) {
      const fieldFilter = filter.groupFilter;
      const filters = fieldFilter?.filters;
      if (filters) {
        const firstFilter = filters[0];
        const fieldChoices = filters.map(filter => filter.singleFilter?.value).filter(value => value !== null) as string[];
        setSelectedOperator(firstFilter.singleFilter?.operator);
        setSelectedFieldChoices(fieldChoices);
      }
    }
  }, [filter]);

  const onOperatorChange = (operator: Operator) => {
    setSelectedOperator(operator);

    if (selectedPathKey && operator && (selectedFieldChoices || operator === IS_EMPTY || operator === IS_NOT_EMPTY)) {
      onFilterChange(selectedPathKey, operator, selectedFieldChoices || []);
    }
  }

  const onValueChange = (fieldChoices: string[]) => {
    const newSelectedFieldChoices = fieldChoices;
    setSelectedFieldChoices(newSelectedFieldChoices);
    if (selectedPathKey && selectedOperator) {
      onFilterChange(selectedPathKey, selectedOperator, newSelectedFieldChoices);
    }
  }

  function onFilterChange(pathKey: string, operator: Operator, fieldChoices: string[]) {
    if (!pathKey || !operator || (!fieldChoices && operator !== IS_EMPTY && operator !== IS_NOT_EMPTY)) {
      return;
    }

    const newFieldFilters: Filter[] = [];
    let operatorToUse = operator;
    if (operator === EQUAL) {
      operatorToUse = HAS;
    }
    if (operator === NOT_EQUAL) {
      operatorToUse = NOT_HAS;
    }

    if (operator === IS_EMPTY || operator === IS_NOT_EMPTY) {
      newFieldFilters.push({
        singleFilter: {
          pathKey: pathKey,
          operator: operator,
          value: null,
          valueKey: null,
          formula: null
        },
        groupFilter: null
      });
    } else {
      fieldChoices.forEach(fieldChoice => {
        newFieldFilters.push({
          singleFilter: {
            pathKey: pathKey,
            operator: operatorToUse,
            value: fieldChoice,
            valueKey: null,
            formula: null
          },
          groupFilter: null
        });
      })
    }

    const fieldFilter: Filter = {
      singleFilter: null,
      groupFilter: {
        filters: newFieldFilters,
        combinator: (operatorToUse === NOT_HAS ? AND : OR)
      }
    }

    validateFilter(fieldFilter);
  }

  const fieldKey = selectedPathKey ? getFieldKey(selectedPathKey) : null;
  const field = fieldKey ? (fieldByKeys?.get(fieldKey.fieldKey) || null) : null;

  const disabledOperator = false;
  return (
    <div className={TW_FilterEditorValue_Wrapper}>
      <Select onValueChange={onOperatorChange} value={selectedOperator} disabled={disabled}>
        <SelectTrigger className={(selectedOperator === IS_EMPTY || selectedOperator === IS_NOT_EMPTY) ? TW_FilterEditorOperatorSelectFull : TW_FilterEditorOperatorSelect} disabled={disabledOperator}>
          <SelectValue placeholder={t("filter.operator.placeholder")} />
        </SelectTrigger>
        <SelectContent>
          {validOperators.map((operatorToDisplay) => {
            let operator = operatorToDisplay;
            if (field?.type === FieldType.Choice) {
              if (operatorToDisplay === EQUAL) {
                operator = HAS;
              }
              if (operatorToDisplay === NOT_EQUAL) {
                operator = NOT_HAS;
              }
            }
            return (
              <SelectItem key={operator} value={operator}>{getOperatorLabel(operatorToDisplay, t)}</SelectItem>
            )
          })}
        </SelectContent>
      </Select>
      {selectedOperator !== IS_EMPTY && selectedOperator !== IS_NOT_EMPTY &&
        <ChoiceSelect field={field} value={selectedFieldChoices} disabled={disabledOperator || disabled} onSelectionChange={onValueChange} />
      }
    </div>
  )
}